import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Shield, Lock, KeyRound, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = ({ setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // First login
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      
      const { access_token, user } = response.data;
      setToken(access_token);
      setUserInfo(user);
      
      // Check if user is admin and if password change is required
      const statusResponse = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      
      if (!statusResponse.data.is_admin) {
        toast.error('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      // Check if password change is required
      if (statusResponse.data.requires_password_change) {
        setRequiresPasswordChange(true);
        setLoading(false);
        return;
      }

      // Complete login
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      toast.success('Welcome, Admin!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/set-admin-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Complete login
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userInfo));
      setUser(userInfo);
      toast.success('Password set successfully. Welcome, Admin!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If an account exists with this email, you will receive a password reset link.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot password screen
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="card" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' }}>
                <Mail size={40} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold mb-2" style={{ color: '#333' }}>Reset Password</h1>
              <p className="text-gray-500 text-sm">Enter your email to receive a reset link</p>
            </div>

            <form onSubmit={handleForgotPassword}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Email
                </label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  placeholder="admin@anniecares.com"
                  className="w-full"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' }}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => setShowForgotPassword(false)}
                className="text-sm text-purple-600 hover:text-purple-800 font-medium"
              >
                Back to Login
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Password change screen
  if (requiresPasswordChange) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="card" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' }}>
                <KeyRound size={40} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold mb-2" style={{ color: '#333' }}>Set Your Password</h1>
              <p className="text-gray-500 text-sm">Create a secure password for your admin account</p>
            </div>

            <form onSubmit={handleSetPassword}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Enter new password"
                  className="w-full"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Confirm password"
                  className="w-full"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' }}
                disabled={loading}
              >
                {loading ? 'Setting Password...' : 'Set Password & Continue'}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="card" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' }}>
              <Shield size={40} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#333' }}>Admin Portal</h1>
            <p className="text-gray-500 text-sm">Annie Cares Administration</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="admin-login-form">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Email
              </label>
              <div className="relative">
                <input
                  data-testid="admin-email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="admin@anniecares.com"
                  className="w-full"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  data-testid="admin-password-input"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className="w-full"
                />
              </div>
            </div>

            <button
              data-testid="admin-submit-button"
              type="submit"
              className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' }}
              disabled={loading}
            >
              {loading ? 'Authenticating...' : 'Access Admin Panel'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t text-center">
            <button
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium mb-3 block w-full"
            >
              Forgot Password?
            </button>
            <p className="text-xs text-gray-500">
              <Lock size={12} className="inline mr-1" />
              Authorized personnel only
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
